import { observer } from "mobx-react";
import { Condition } from "./Condition";
import { CancelRounded } from "@mui/icons-material";
import {
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Tooltip,
} from "@mui/material";
import React, { createContext, useContext } from "react";
import { StepOutputVariableSelector } from "../StepOutputVariableSelector";
import {
    ConditionPrimitive as ConditionPrimitiveClass,
    ConditionPropertyType,
} from "models/LLMs";
import { FullWidthGrid } from "../../FullWidthGrid";
import { useCurrentStep } from "../Step";
import { ConditionExpressionType } from "models/LLMs/LogicChaining/Conditions/ConditionExpressionType";
import {
    ConditionExpressionEntry,
    expressionTakesValue,
    orderedConditionExpressions,
} from "models/LLMs/LogicChaining/Conditions/ConditionExpressionMeta";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "components/Classifiers/LLMClassifiers/Stores/LLMClassifierBuilderStore";
import {
    getDefaultVariableValue,
    groupVariablesByProducingLLMModel,
    isModelVariable,
    matchesLLMVariableName,
    LLMVariableType,
    SlimLLMVariable,
} from "models/LLMs/LLMVariable";
import { LLMVariableValueField } from "../../LLMVariableValueField";
import { ChainSourceStore } from "../../../Stores/ChainSourceStore";

const expressionTypeOption = (expression: ConditionExpressionEntry) => ({
    label: expression.label,
    value: expression.type,
});

/**
 * A structured output's value is only tested for presence.
 */
const presenceExpressionTypeOptions = orderedConditionExpressions
    .filter((expression) => !expression.takesValue)
    .map(expressionTypeOption);

const commonExpressionTypeOptions = orderedConditionExpressions
    .filter((expression) => !expression.isStringComparison)
    .map(expressionTypeOption);

const expressionTypeOptions = [
    ...commonExpressionTypeOptions,
    ...orderedConditionExpressions
        .filter((expression) => expression.isStringComparison)
        .map(expressionTypeOption),
];

const propertyTypeOptions = [
    { label: "Variable", value: ConditionPropertyType.Step },
    { label: "Metadata", value: ConditionPropertyType.Conversation },
];

type ConditionVariableOption = SlimLLMVariable & { parent?: string };

/** Shows the part read-only, for a condition owned by another section. */
type DisabledProp = { disabled?: boolean };

const lockedModelConditionTooltip =
    "Add this model as a parent to change the condition source from the Builder step.";

const ConditionPrimitiveContext = createContext<ConditionPrimitiveClass | null>(
    null,
);

function useConditionPrimitive() {
    const context = useContext(ConditionPrimitiveContext);
    if (!context) {
        throw new Error(
            "useConditionPrimitive must be used within a ConditionPrimitiveProvider",
        );
    }
    return context;
}

interface ConditionPrimitiveProps {
    condition: ConditionPrimitiveClass;
}

export function ConditionPrimitive(
    props: React.PropsWithChildren<ConditionPrimitiveProps>,
) {
    return (
        <ConditionPrimitiveContext.Provider value={props.condition}>
            <Condition.Container>{props.children}</Condition.Container>
        </ConditionPrimitiveContext.Provider>
    );
}

const ConditionPrimitivePropertyType = observer(() => {
    const condition = useConditionPrimitive();
    const store = useStore(LLMClassifierBuilderStore);
    const chainSourceStore = useStore(ChainSourceStore);

    const propertyTypeOption = propertyTypeOptions.find(
        (option) => option.value === condition.propertyType,
    );
    const selectedVariable = store.variableGroups
        .flatMap((group) => group.variables)
        .find((variable) =>
            matchesLLMVariableName(variable, condition.propertyName),
        );
    const isUnparentedModelCondition =
        isModelVariable(condition.propertyName) &&
        (!selectedVariable ||
            !chainSourceStore.isVariableReferenceable(selectedVariable));

    return (
        <FullWidthGrid item>
            <Tooltip
                title={
                    isUnparentedModelCondition
                        ? lockedModelConditionTooltip
                        : ""
                }
                disableHoverListener={!isUnparentedModelCondition}
            >
                <div>
                    <AcxSelectSingle
                        id="propertyType"
                        fullWidth
                        labelField="label"
                        valueField="value"
                        options={propertyTypeOptions}
                        defaultValue={propertyTypeOption}
                        isDisabled={isUnparentedModelCondition}
                        onChange={(option) => {
                            condition.setPropertyType(option.value);
                        }}
                    />
                </div>
            </Tooltip>
        </FullWidthGrid>
    );
});

/**
 * Renders either the `ConditionPrimitiveStepPropertyName` or `ConditionPrimitiveMetadataPropertyName` component based
 * on the `propertyType` of the condition. If you want to render a different component,
 * you can use the `ConditionPrimitiveStepPropertyName` or `ConditionPrimitiveMetadataPropertyName` components directly.
 */
const ConditionPrimitivePropertyName = observer(() => {
    const condition = useConditionPrimitive();

    const isStepCondition =
        condition.propertyType === ConditionPropertyType.Step;
    const isMetadataCondition =
        condition.propertyType === ConditionPropertyType.Conversation;

    return (
        <FullWidthGrid item>
            {isStepCondition && <ConditionPrimitiveStepPropertyName />}
            {isMetadataCondition && <ConditionPrimitiveMetadataPropertyName />}
        </FullWidthGrid>
    );
});

/**
 * Renders a step output variable selector for the property name. Relies on the `useCurrentStep` hook so it must be used within a `Step` component.
 */
const ConditionPrimitiveStepPropertyName = observer(() => {
    const condition = useConditionPrimitive();
    const step = useCurrentStep();

    return (
        <FullWidthGrid item>
            <StepOutputVariableSelector
                step={step}
                variable={condition.shortPropertyName}
                onChange={(variable) => {
                    condition.setShortPropertyName(variable);
                    condition.setPropertyName(step.getPropertyName(variable));
                }}
            />
        </FullWidthGrid>
    );
});

/**
 * Renders a metadata selector for the property name.
 *
 * `excludeModelVariables` leaves out Core AI and LLM model outputs. Used by the
 * Volume control's Metadata Filters, where gating on a model output is owned by
 * the Chain source / parent models section instead.
 */
const ConditionPrimitiveMetadataPropertyName = observer(
    (props: { excludeModelVariables?: boolean } & DisabledProp) => {
        const condition = useConditionPrimitive();
        const store = useStore(LLMClassifierBuilderStore);
        const chainSourceStore = useStore(ChainSourceStore);

        const metadataOption = store.variableGroups
            .flatMap((group) => group.variables)
            .find((variable) =>
                matchesLLMVariableName(variable, condition.propertyName),
            );
        const isLegacyModelCondition =
            props.excludeModelVariables &&
            isModelVariable(condition.propertyName);
        const isUnparentedModelCondition =
            isModelVariable(condition.propertyName) &&
            (!metadataOption ||
                !chainSourceStore.isVariableReferenceable(metadataOption));
        const isSourceLocked =
            isLegacyModelCondition || isUnparentedModelCondition;
        const conditionVariableGroups = groupVariablesByProducingLLMModel(
            store.variableGroups
                .map((group) => ({
                    ...group,
                    variables: group.variables.filter(
                        (variable) =>
                            chainSourceStore.isVariableReferenceable(
                                variable,
                            ) &&
                            !(
                                props.excludeModelVariables &&
                                isModelVariable(variable.name)
                            ),
                    ),
                }))
                .filter((group) => group.variables.length > 0),
        ).map((group) => ({
            label: group.label,
            options: group.variables.map<ConditionVariableOption>(
                (variable) => ({
                    ...variable,
                    // AcxSelect searches this field in addition to the option label.
                    parent: variable.modelOutputSource?.classifierName,
                }),
            ),
        }));

        return (
            <FullWidthGrid item>
                <Tooltip
                    title={isSourceLocked ? lockedModelConditionTooltip : ""}
                    disableHoverListener={!isSourceLocked || props.disabled}
                >
                    <div>
                        <AcxSelectSingle
                            id=""
                            fullWidth
                            labelField="label"
                            valueField="name"
                            options={conditionVariableGroups}
                            defaultValue={metadataOption as any}
                            isDisabled={isSourceLocked || props.disabled}
                            onChange={(option: {
                                label: string;
                                name: string;
                            }) => {
                                const propertyName = option.name;
                                const variable = store.variableGroups
                                    .flatMap((group) => group.variables)
                                    .find(
                                        (variable) =>
                                            variable.name === propertyName,
                                    );

                                if (!variable)
                                    throw new Error(
                                        "Unknown variable selected.",
                                    );

                                condition.setShortPropertyName(option.label);
                                condition.setPropertyName(propertyName);
                                condition.setClassifierId(
                                    variable.modelOutputSource?.classifierId,
                                );

                                if (variable.presenceOnly) {
                                    // Presence is the only comparison this variable offers,
                                    // so carrying over an equality would leave the condition
                                    // on an operation its selector no longer lists.
                                    condition.setOperation(
                                        ConditionExpressionType.Exists,
                                    );
                                    condition.setValue("");
                                    return;
                                }

                                condition.setValue(
                                    getDefaultVariableValue(
                                        variable.type ?? LLMVariableType.String,
                                    ).toString(),
                                );
                            }}
                        />
                    </div>
                </Tooltip>
            </FullWidthGrid>
        );
    },
);

const ConditionPrimitiveExpressionType = observer((props: DisabledProp) => {
    const condition = useConditionPrimitive();
    const store = useStore(LLMClassifierBuilderStore);
    const metadataOption = store.nonExtendedMetadataVariables.find((o) =>
        matchesLLMVariableName(o, condition.propertyName),
    );

    let availableOptions = expressionTypeOptions;
    if (metadataOption?.presenceOnly) {
        availableOptions = presenceExpressionTypeOptions;
    } else if (
        metadataOption &&
        (metadataOption.type === LLMVariableType.Boolean ||
            (metadataOption.values && metadataOption.values.length > 0))
    ) {
        //don't need string contains ops for boolean or enumerated values
        availableOptions = commonExpressionTypeOptions;
    }

    return (
        <FullWidthGrid item>
            <AcxSelectSingle
                id=""
                fullWidth
                labelField="label"
                valueField="value"
                options={availableOptions}
                isDisabled={props.disabled}
                defaultValue={availableOptions.find(
                    (option) => option.value === condition.operation,
                )}
                onChange={(option) => {
                    condition.setOperation(option.value);
                }}
            />
        </FullWidthGrid>
    );
});

const ConditionPrimitiveValue = observer((props: DisabledProp) => {
    const condition = useConditionPrimitive();
    const store = useStore(LLMClassifierBuilderStore);

    if (!expressionTakesValue(condition.operation)) {
        return null;
    }

    const metadataOption = store.nonExtendedMetadataVariables.find((o) =>
        matchesLLMVariableName(o, condition.propertyName),
    );

    return (
        <FullWidthGrid item>
            <LLMVariableValueField
                id=""
                fullWidth
                variable={metadataOption}
                value={condition.value}
                disabled={props.disabled}
                onChange={(value) => condition.setValue(value)}
            />
        </FullWidthGrid>
    );
});

const ConditionPrimitiveIsCaseSensitive = observer((props: DisabledProp) => {
    const condition = useConditionPrimitive();

    if (!expressionTakesValue(condition.operation)) {
        return null;
    }

    return (
        <Grid item>
            <FormControlLabel
                label="Is case sensitive"
                sx={{ width: "fit-content", whiteSpace: "nowrap" }}
                control={
                    <Checkbox
                        disabled={props.disabled}
                        onChange={(event) =>
                            condition.setIsCaseSensitive(event.target.checked)
                        }
                        checked={condition.isCaseSensitive}
                    />
                }
            />
        </Grid>
    );
});

interface ConditionPrimitiveRemoveProps {
    onRemove: (condition: ConditionPrimitiveClass) => void;
}

const ConditionPrimitiveRemove = observer(
    (props: ConditionPrimitiveRemoveProps) => {
        const condition = useConditionPrimitive();

        return (
            <Grid item>
                <Tooltip title="Remove condition">
                    <IconButton
                        size="small"
                        onClick={() => {
                            props.onRemove(condition);
                        }}
                    >
                        <CancelRounded fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Grid>
        );
    },
);

ConditionPrimitive.PropertyType = ConditionPrimitivePropertyType;
ConditionPrimitive.PropertyName = ConditionPrimitivePropertyName;
ConditionPrimitive.StepPropertyName = ConditionPrimitiveStepPropertyName;
ConditionPrimitive.MetadataPropertyName =
    ConditionPrimitiveMetadataPropertyName;
ConditionPrimitive.ExpressionType = ConditionPrimitiveExpressionType;
ConditionPrimitive.Value = ConditionPrimitiveValue;
ConditionPrimitive.IsCaseSensitive = ConditionPrimitiveIsCaseSensitive;
ConditionPrimitive.Remove = ConditionPrimitiveRemove;
